import { Fragment } from 'react'
import { AssetReference } from '@opallabs/cassini'
import { Asset } from '@opallabs/kuiper'
import { AccountName } from '../../../../../AccountName'
import { ContentAvatar } from '../../../../../ContentAvatar'
import { ContentTextInput } from '../../../../../ContentTextInput'
import { DragAndDropAssetGallery } from '../../../../../DragAndDropAssetGallery'
import { twitterTweetPositions } from '../../../../../DragAndDropAssetGallery/positions'
import { LinkPreviewAssetCarousel } from '../../../../../LinkPreviewAssetCarousel'
import { twitterTweetMobileFields } from '../../../../../LinkPreviewAssetCarousel/fields'
import { useContentWithLinkPreviewAssetCarousel } from '../../../../helpers/hooks'
import {
  LinkWithData,
  OnLinkDataUpdate,
  OnShowAssetPickerProps,
} from '../../../../helpers/types'
import * as styles from './styles'

const MAX_ASSETS = 4
const CONTENT_CHARACTER_LIMIT = 300

export interface Props {
  accountImage: AssetReference | null
  accountName: string
  accountUsername: string
  assets: AssetReference[]
  content: string
  display: string
  isPresenting: boolean
  linksWithData: LinkWithData[]
  onAssetChange: (assets: AssetReference[]) => void
  onContentChange: (val: string) => void
  onExpandAsset: (assetId: number) => void
  onLinkDataUpdate: OnLinkDataUpdate
  onRemoveAsset: (assetId: number) => void
  onShowAssetPicker: (args: OnShowAssetPickerProps) => void
  readOnly: boolean
  updateCaption: (value: string, assetId: number) => void
}

interface HeaderProps {
  accountImage: AssetReference | null
  accountName: string
  accountUsername: string
}

const BlueskyHeader: React.FC<HeaderProps> = ({
  accountImage,
  accountName,
  accountUsername,
}) => (
  <div css={styles.headerWrapper}>
    <ContentAvatar assetReference={accountImage} size={40} />
    <AccountName
      name={accountName}
      size={15}
      color={styles.blueskyColors.darkText}
      style={styles.accountName}
    />
    <span css={styles.accountHandle}>
      {accountUsername ? `@${accountUsername}` : ''} · 1m
    </span>
  </div>
)

export const Single: React.FC<Props> = ({
  accountImage,
  accountName,
  accountUsername,
  assets,
  content,
  display,
  isPresenting = false,
  linksWithData,
  onAssetChange,
  onContentChange,
  onExpandAsset,
  onLinkDataUpdate,
  onRemoveAsset,
  onShowAssetPicker,
  readOnly,
  updateCaption,
}) => {
  const { link, isPersistingLink, setIsPersistingLink, linkWithData } =
    useContentWithLinkPreviewAssetCarousel({
      content,
      linksWithData,
    })
  const linkAssets = linkWithData ? linkWithData.assetReferences : []

  return (
    <Fragment>
      <BlueskyHeader
        accountImage={accountImage}
        accountName={accountName}
        accountUsername={accountUsername}
      />
      <ContentTextInput
        characterLimit={CONTENT_CHARACTER_LIMIT}
        readOnly={readOnly}
        value={content}
        highlightSocialTags={true}
        onChange={onContentChange}
        style={styles.contentInput}
      />
      {assets.length === 0 && isPersistingLink && (
        <div css={styles.contentWrapper}>
          <LinkPreviewAssetCarousel
            assetStyle={styles.getLinkAssetStyle(linkAssets.length > 0)}
            disableRemoveLink={false}
            fields={twitterTweetMobileFields}
            domainStyle={styles.linkDomainStyle}
            hasRemovedLink={() => setIsPersistingLink(false)}
            hasSquareInlineAsset={false}
            link={link}
            linkStyle={styles.getLinkStyle(linkAssets.length > 0)}
            linkWithData={linkWithData}
            onLinkDataUpdate={onLinkDataUpdate}
            onShowAssetPicker={onShowAssetPicker}
            readOnly={readOnly || isPresenting}
            titleStyle={styles.linkTitleStyle}
          />
        </div>
      )}
      {assets.length === 1 && (
        <div css={styles.contentWrapper}>
          <Asset
            assetReference={assets[0]}
            hasAssetOptions={true}
            hasAssetOverlay={assets[0] && (!readOnly || isPresenting)}
            imageFit="cover"
            onEmbiggen={() => onExpandAsset(assets[0].id || 0)}
            onRemove={() => onRemoveAsset(assets[0].id || 0)}
            overrideStyles={styles.getAssetStyles(assets[0])}
            placeholder={true}
            removeable={!isPresenting}
            updateCaption={updateCaption}
          />
        </div>
      )}
      {assets.length > 1 && (
        <div css={styles.contentWrapper}>
          <DragAndDropAssetGallery
            assets={assets}
            display={display}
            galleryContainerStyles={styles.galleryContainer}
            hasAssetOptions={true}
            isPresenting={isPresenting}
            maxAssets={MAX_ASSETS}
            maxHeight={305}
            maxWidth={410}
            onAssetChange={onAssetChange}
            onExpandAsset={onExpandAsset}
            onRemoveAsset={onRemoveAsset}
            positions={twitterTweetPositions}
            readOnly={readOnly}
            updateCaption={updateCaption}
          />
        </div>
      )}
    </Fragment>
  )
}
